import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OnOffSwitch } from "@/components/OnOffSwitch";
import { OrderList } from "@/components/OrderList";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";

const LISTS = [
  { type: "destinations", title: "Tithe Destinations", help: "Where tithes are sent (e.g. General Council, MWJK)." },
  { type: "giver_types", title: "Pastor / Church / Guest(s) / Ritel", help: "Choices for who the tithe is from, shown as a dropdown in the New Tithe form." },
  { type: "jemdron", title: "Jemdron", help: "Jemdron names for the optional Jemdron dropdown under Church in the New Tithe form (both General Council and MWJK)." },
  { type: "categories", title: "Conference Collection Categories", help: "Tithe, offering, special collection, conference fee..." },
  { type: "collections", title: "Collections", help: "Event or drive names (building fund, youth camp...). Set a Goal to track it in Reports → Conference Summary." },
];

function GoalInput({ item, save }) {
  const [g, setG] = useState(item.goal ?? "");
  const commit = () => { const v = g === "" ? null : Number(g); if (v !== (item.goal ?? null)) save({ goal: v }); };
  return (
    <label className="flex items-center gap-1 text-xs font-semibold text-slate-500">Goal per church $
      <Input type="number" min="0" step="0.01" value={g} onChange={(e) => setG(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } }} placeholder="—" className="h-8 w-28 bg-white text-slate-900" data-testid={`option-goal-${item.id}`} />
    </label>
  );
}

function OptionItem({ item, type, onChanged }) {
  const [editing, setEditing] = useState(false);
  const { feat } = useApp();
  const [name, setName] = useState(item.name);
  const save = async (patch) => {
    try { await api.put(`/options/${type}/${item.id}`, { name: item.name, active: item.active, goal: item.goal ?? null, ...patch }); setEditing(false); if ("goal" in patch) toast.success("Goal saved"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => { try { await api.delete(`/options/${type}/${item.id}`); onChanged(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2">
      {editing ? (
        <form className="flex min-w-40 flex-1 gap-1" onSubmit={(e) => { e.preventDefault(); save({ name }); }}>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 bg-white text-slate-900" autoFocus data-testid={`option-rename-${item.id}`} />
          <Button size="icon" type="submit" className="h-9 w-9" data-testid={`option-rename-save-${item.id}`}><Check className="h-4 w-4" /></Button>
        </form>
      ) : (
        <span className={`min-w-[8rem] flex-1 break-words font-medium ${item.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{item.name}</span>
      )}
      {type === "collections" && feat("conference_goals") && <GoalInput item={item} save={save} />}
      <OnOffSwitch checked={item.active} onChange={(v) => save({ active: v })} testid={`option-active-${item.id}`} />
      {!editing && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing(true)} data-testid={`option-edit-${item.id}`}><Pencil className="h-4 w-4" /></Button>}
      <ConfirmDelete onConfirm={remove} title={`Delete “${item.name}”?`} message="Old records keep this name." testid={`option-delete-${item.id}`} />
    </div>
  );
}

function ListEditor({ type, title, help }) {
  const ctx = useApp();
  const items = ctx.options[type];
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post(`/options/${type}`, { name, active: true }); setName(""); ctx.reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const saveOrder = async (ids) => { try { await api.put(`/options/${type}/reorder`, { ids }); ctx.reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="space-y-3 rounded-xl border border-slate-200 bg-[hsl(var(--sand))] p-4" data-testid={`list-${type}`}>
      <div>
        <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
        <p className="text-xs text-slate-500">{help} Turn OFF to hide from forms.</p>
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add new..." className="h-10 bg-white text-slate-900 placeholder:text-slate-400" data-testid={`list-${type}-input`} />
        <Button type="submit" className="h-10" data-testid={`list-${type}-add`}><Plus className="h-4 w-4" /></Button>
      </form>
      <OrderList items={items} onSave={saveOrder} testid={`opt-${type}`} renderItem={(it) => <OptionItem item={it} type={type} onChanged={ctx.reload} />} />
    </section>
  );
}

export default function AdminLists() {
  return <div className="grid gap-4 md:grid-cols-2">{LISTS.map((l) => <ListEditor key={l.type} {...l} />)}</div>;
}
